"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import ReadOnlyInput from "../../../../components/ReadOnlyInput";
import Dropdown from "../../../../components/Dropdown";
import PharmacistSignatureModal from "../../../global/view/PharmacistSignatureModal";
import ActionTile from "../../Chronic/components/ActionTile";
import FeeSummaryCard from "../../Chronic/components/FeeSummaryCard";
import DeliveryOptions from "../../Chronic/components/DeliveryOptions";
import styles from "../css/otcHistoryDetails.module.css";
import { fetchScriptDetails } from "../../../../api/dispensary/script/processScriptApi";
import { fetchPrescriptionPayment } from "../../../../api/medicalHistory/medicalHistoryApi";

export default function OTCHistoryDetailsView({
  scriptData: propScriptData,
  showTotalItems = false,
  invoiceActionLabel = "View Invoice",
  hideRadios = false,
  hideMedicationLabel = false,
  hidePackageLabel = false,
  hideViewScriptDocument = false,
  deliveryLabelText = "Delivery Label",
  onDeliveryAction, // NEW: callback for Submit Claim
  onInvoiceSigned, // NEW: optional callback when pharmacist signature completed,
  centerTopControls = false,
  usePrescriptionPaymentDelivery = false,
  readOnlyDelivery = false,
  forceHideLabelButtons = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { scriptData: navScriptData } = location.state || {};
  const [loadedData, setLoadedData] = useState(null);
  const baseData = navScriptData || propScriptData || {};
  const scriptData = loadedData || baseData || {};
  const [deliveryOption, setDeliveryOption] = useState("Collect");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null); // Persist pharmacist signature
  const rowsPerPage = 50;

  useEffect(() => {
    const firstNumeric = (val) => {
      if (val == null) return null;
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    };
    const idNumeric = firstNumeric(baseData?.id)
      ?? firstNumeric(baseData?.scriptId)
      ?? firstNumeric(baseData?.scriptNo)
      ?? firstNumeric(baseData?.scriptNumber);
    if (!idNumeric) return;
    let alive = true;
    (async () => {
      try {
        const data = await fetchScriptDetails(String(idNumeric));
        if (!alive || !data) return;
        const pharmacist = data.pharmacist
          ? `${data.pharmacist.firstname || ""} ${data.pharmacist.surname || ""}`.trim()
          : "";
        const practitioner = data.practitioner
          ? `${data.practitioner.title ? data.practitioner.title + " " : ""}${data.practitioner.firstname || ""} ${data.practitioner.surname || ""}`.trim()
          : "";
        const transformed = {
          scriptNumber: String(baseData?.scriptNumber || data.script?.id || idNumeric),
          date: data.script?.date || baseData?.date || "",
          paymentType: data.script?.paymentType || baseData?.paymentType || "",
          practitioner,
          pharmacist,
          drugs: Array.isArray(data.drugs) ? data.drugs : [],
        };
        setLoadedData(transformed);
      } catch (e) {
        console.error("Failed to fetch OTC details", e);
      }
    })();
    return () => { alive = false; };
  }, [baseData?.scriptNumber, baseData?.scriptNo, baseData?.scriptId, baseData?.id]);

  // Load prescription payment (delivery option + preferred date/time) if present and enabled
  useEffect(() => {
    if (!usePrescriptionPaymentDelivery) return;

    const firstNumeric = (val) => {
      if (val == null) return null;
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    };
    const idNumeric = firstNumeric(baseData?.id)
      ?? firstNumeric(baseData?.scriptId)
      ?? firstNumeric(baseData?.scriptNo)
      ?? firstNumeric(baseData?.scriptNumber);
    if (!idNumeric) return;

    let alive = true;
    (async () => {
      try {
        const payment = await fetchPrescriptionPayment(String(idNumeric));
        if (!alive || !payment) return;

        if (payment.deliveryOption) {
          setDeliveryOption(payment.deliveryOption);
        }
        if (payment.preferredDate) {
          setPreferredDate(payment.preferredDate);
        }
        if (payment.preferredTime) {
          setPreferredTime(payment.preferredTime);
        }
      } catch (e) {
        console.error("Failed to fetch prescription payment (OTC)", e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [usePrescriptionPaymentDelivery, baseData?.scriptNumber, baseData?.scriptNo, baseData?.scriptId, baseData?.id]);

  // Fallback data
  const scriptNumber = scriptData?.scriptNumber || "SCR001-1";
  const pharmacist = scriptData?.pharmacist || "";
  const paymentType = scriptData?.paymentType || "";
  const practitioner = scriptData?.practitioner || "";

  const tableData = Array.isArray(scriptData?.drugs) && scriptData.drugs.length > 0
    ? scriptData.drugs.map((d) => ({
        drugName: d.drugName || "",
        packSize: d.packSize || "",
        icd10: d.icd10 || "",
        strength: d.strength || "",
        qty: d.quantity || d.qty || 0,
        nappiCode: d.nappiCode || "",
        instructions: d.instructions || "",
        costPrice: d.costPrice || "",
      }))
    : [];

  const totalPages = Math.ceil(tableData.length / rowsPerPage);
  const paginatedData = tableData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const columns = [
    { header: "Drug Name", accessor: "drugName" },
    { header: "Pack Size", accessor: "packSize" },
    { header: "ICD-10", accessor: "icd10" },
    { header: "Strength", accessor: "strength" },
    { header: "Qty", accessor: "qty" },
    { header: "Nappi Code", accessor: "nappiCode" },
    { header: "Instructions", accessor: "instructions" },
    { header: "Cost Price", accessor: "costPrice" },
  ];

  const parseMoney = (v) => {
    if (v == null) return 0;
    if (typeof v === 'string') {
      const cleaned = v.replace(/^[^\d.-]*/, '');
      const n = parseFloat(cleaned);
      return Number.isFinite(n) ? n : 0;
    }
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const dispensingFeeNum = 15;
  const itemsSum = tableData.reduce((s, r) => s + parseMoney(r.costPrice) * (parseMoney(r.qty) || 1), 0);
  const grossAmount = (itemsSum + dispensingFeeNum).toFixed(2);
  const dispensingFee = dispensingFeeNum.toFixed(2);

  const handleViewScriptDocument = () => console.log("View Script Document");
  const handleViewInvoice = () => {
    if (invoiceActionLabel === "Sign Invoice") {
      setShowSignatureModal(true);
    } else {
      console.log("View Invoice");
    }
  };

  const handleSignatureNext = (signatureData) => {
    console.log("Signature saved:", signatureData);
    setSignatureImage(signatureData);
    setShowSignatureModal(false);
    if (typeof onInvoiceSigned === "function") {
      onInvoiceSigned(signatureData);
    }
  };

  const handleMedicationLabel = () => console.log("Generate Medication Label");
  const handleDeliveryLabel = () => console.log("Generate Delivery Label");
  const handlePackageAndLabel = () => navigate("/customer/dispensary/package-script");

  const isDeliver = deliveryOption === "Deliver";

  return (
    <div className={styles.page}>
      <Navbar />
      <main className={styles.main}>
        {/* Top controls */}
        <div
          className={styles.controlsRow}
          style={centerTopControls ? { gridTemplateColumns: "repeat(4, 220px)", justifyContent: "center" } : undefined}
        >
          {/* <ReadOnlyInput label="Script Number" value={scriptNumber} />
          {showTotalItems && (
            <ReadOnlyInput
              label="Total Items"
              value={scriptData?.drugs?.length || tableData.length}
            />
          )} */}
          <div className={styles.field}>
            <ReadOnlyInput label="Pharmacist" value={pharmacist} />
          </div>
          <div className={styles.field}>
            <ReadOnlyInput label="Payment Type" value={paymentType} />
          </div>
          <div className={styles.field}>
            <ReadOnlyInput label="Practitioner" value={practitioner} />
          </div>
        </div>
      
        {/* Table */}
        <div className={styles.tableCard}>
          <Table columns={columns} data={paginatedData} />
        </div>

        <br />

        {/* Fees left; actions + delivery on right */}
        <div className={styles.midRow}>
          <div className={styles.feesLeft}>
            <FeeSummaryCard dispensingFee={`R ${dispensingFee}`} gross={`R ${grossAmount}`} />
          </div>
          <div className={styles.tilesMiddle}>
            <div className={styles.tilesCol}>
              {invoiceActionLabel && (
                <ActionTile title={invoiceActionLabel === "Sign Invoice" ? "Sign Invoice" : "Invoice" } rightLinkLabel="View" onRightLinkClick={handleViewInvoice} />
              )}
            </div>
          </div>
          <div className={styles.optionsRight}>
            {!hideRadios && (
              readOnlyDelivery ? (
                <DeliveryOptions
                  mode={deliveryOption}
                  onModeChange={() => {}}
                  preferredDate={preferredDate}
                  onDateChange={() => {}}
                  preferredTime={preferredTime}
                  onTimeChange={() => {}}
                  timeSlots={preferredTime ? [preferredTime] : undefined}
                  readOnly={true}
                  hideUnselected={true}
                />
              ) : (
                <DeliveryOptions
                  mode={deliveryOption}
                  onModeChange={setDeliveryOption}
                  preferredDate={preferredDate}
                  onDateChange={setPreferredDate}
                  preferredTime={preferredTime}
                  onTimeChange={setPreferredTime}
                  hideUnselected={false}
                />
              )
            )}
          </div>
        </div>

        {/* Bottom buttons */}
        {!forceHideLabelButtons && (
          <div className={styles.bottomButtons}>
            {!hideMedicationLabel && (
              <button className={styles.btnPrimary} onClick={handleMedicationLabel}>Medication Label</button>
            )}
            <button
              className={styles.btnPrimary}
              onClick={() => {
                if (typeof onDeliveryAction === "function") {
                  onDeliveryAction();
                } else {
                  handleDeliveryLabel();
                }
              }}
            >
              {deliveryLabelText}
            </button>
            {!hidePackageLabel && !isDeliver && (
              <button className={styles.btnPrimary} onClick={handlePackageAndLabel}>Package and Label</button>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </main>
      <Footer />

      <PharmacistSignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onNext={handleSignatureNext}
        initialSignature={signatureImage}
      />
    </div>
  );
}